import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { ChevronDown } from "lucide-react";

import { HubMockupBadge } from "@/components/HubMockupBadge";
import {
  hubMockupBadgeVisibleForSurface,
  mrpHubPageSlugFromNavPath,
  type HubMockupDocument,
} from "@/lib/hubMockupApi";
import { moduleNavMoreItems, moduleNavPrimaryItems, type NavItem } from "@/lib/appNavigation";
import {
  computeHubModuleNavVisibleCount,
  HUB_MODULE_NAV_MORE_LABEL_RESERVE_PX,
} from "@/components/hubModuleNavVisibleCount";
import {
  APP_SWITCHER_MENU_Z_INDEX,
  computeHorizontalMenuPanel,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

function insideSalesNavActive(pathname: string, itemTo: string): boolean {
  if (itemTo === "/inside-sales/sales-orders") {
    return (
      pathname === "/inside-sales/sales-orders" ||
      pathname === "/inside-sales/sales-orders/new" ||
      (pathname.startsWith("/inside-sales/sales-orders/") &&
        !pathname.startsWith("/inside-sales/sales-orders/approvals") &&
        !pathname.startsWith("/inside-sales/sales-orders/settings") &&
        !pathname.startsWith("/inside-sales/sales-orders/rep-assignment"))
    );
  }
  if (itemTo === "/inside-sales/sales-orders/approvals") {
    return pathname.startsWith("/inside-sales/sales-orders/approvals");
  }
  if (itemTo === "/inside-sales/sales-orders/rep-assignment") {
    return pathname.startsWith("/inside-sales/sales-orders/rep-assignment");
  }
  return pathname === itemTo || pathname.startsWith(`${itemTo}/`);
}

function clampQcNavActive(pathname: string, itemTo: string): boolean {
  if (itemTo === "/clamp-qc/settings") {
    return pathname === "/clamp-qc/settings";
  }
  if (itemTo === "/clamp-qc/inspection") {
    return (
      pathname === "/clamp-qc/inspection" ||
      pathname.startsWith("/clamp-qc/jobs/") ||
      pathname.startsWith("/clamp-qc/work-orders/")
    );
  }
  if (itemTo === "/clamp-qc/reporting") {
    return pathname.startsWith("/clamp-qc/reporting");
  }
  return false;
}

function navItemActive(
  app: string,
  pathname: string,
  itemTo: string,
  isActive: boolean,
): boolean {
  return app === "clampqc"
    ? clampQcNavActive(pathname, itemTo)
    : app === "inside_sales"
      ? insideSalesNavActive(pathname, itemTo)
      : isActive;
}

function navLinkClassName(active: boolean): string {
  return cn(
    "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    active ? "bg-brand-50 text-brand-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
  );
}

function HubNavLink({
  item,
  app,
  pathname,
  hubNavDocument,
  onNavigate,
  className,
}: {
  item: NavItem;
  app: string;
  pathname: string;
  hubNavDocument: HubMockupDocument | null | undefined;
  onNavigate?: () => void;
  className?: string;
}) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(navLinkClassName(navItemActive(app, pathname, item.to, isActive)), className)
      }
    >
      {item.label}
      {app === "mrp" && hubMockupBadgeVisibleForSurface(hubNavDocument, "mrp", mrpHubPageSlugFromNavPath(item.to)) ? (
        <HubMockupBadge variant="nav" />
      ) : null}
      {app === "manufacturing" &&
      item.to === "/manufacturing/kitting" &&
      hubMockupBadgeVisibleForSurface(hubNavDocument, "manufacturing", "kitting") ? (
        <HubMockupBadge variant="nav" />
      ) : null}
    </NavLink>
  );
}

export function HubModuleNav({
  app,
  navItems,
  pathname,
  hubNavDocument,
  onNavigate,
}: {
  app: string;
  navItems: NavItem[];
  pathname: string;
  hubNavDocument: HubMockupDocument | null | undefined;
  onNavigate: () => void;
}) {
  const navRef = useRef<HTMLElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const moreTriggerRef = useRef<HTMLButtonElement | null>(null);
  const moreMenuRef = useRef<HTMLUListElement | null>(null);
  const primaryItems = moduleNavPrimaryItems(navItems);
  const pinnedMoreItems = moduleNavMoreItems(navItems);
  const labeledMore = pinnedMoreItems.length > 0;
  const [visibleCount, setVisibleCount] = useState(primaryItems.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreMenuBox, setMoreMenuBox] = useState<AnchoredMenuPanelBox | null>(null);

  const recomputeVisible = useCallback(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    if (!nav || !measure) {
      return;
    }
    const children = Array.from(measure.children) as HTMLElement[];
    const widths = children.map((child) => child.offsetWidth);
    setVisibleCount(
      computeHubModuleNavVisibleCount(widths, nav.clientWidth, {
        alwaysShowMore: pinnedMoreItems.length > 0,
        moreButtonReservePx: labeledMore ? HUB_MODULE_NAV_MORE_LABEL_RESERVE_PX : undefined,
      }),
    );
  }, [labeledMore, pinnedMoreItems.length]);

  useLayoutEffect(() => {
    recomputeVisible();
  }, [navItems, recomputeVisible]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      recomputeVisible();
    });
    observer.observe(nav);
    return () => {
      observer.disconnect();
    };
  }, [recomputeVisible]);

  useEffect(() => {
    if (!moreOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (moreTriggerRef.current?.contains(event.target as Node)) {
        return;
      }
      if (moreMenuRef.current?.contains(event.target as Node)) {
        return;
      }
      setMoreOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [moreOpen]);

  useLayoutEffect(() => {
    if (!moreOpen) {
      setMoreMenuBox(null);
      return;
    }
    const reposition = (): void => {
      if (!moreTriggerRef.current) {
        setMoreMenuBox(null);
        return;
      }
      setMoreMenuBox(
        computeHorizontalMenuPanel({
          anchorRect: moreTriggerRef.current.getBoundingClientRect(),
          menuWidth: 14 * 16,
          horizontalAlign: "end",
          gap: 4,
          maxHeightCap: Math.min(420, Math.floor(window.innerHeight * 0.88)),
        }),
      );
    };
    reposition();
    const onLayout = (): void => {
      reposition();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    window.visualViewport?.addEventListener("resize", onLayout);
    window.visualViewport?.addEventListener("scroll", onLayout);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
      window.visualViewport?.removeEventListener("resize", onLayout);
      window.visualViewport?.removeEventListener("scroll", onLayout);
    };
  }, [moreOpen]);

  const overflowItems = [...primaryItems.slice(visibleCount), ...pinnedMoreItems];
  const overflowActive = overflowItems.some((item) =>
    navItemActive(
      app,
      pathname,
      item.to,
      pathname === item.to || (item.end !== true && pathname.startsWith(`${item.to}/`)),
    ),
  );

  return (
    <nav ref={navRef} className="relative flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none absolute left-0 top-0 flex gap-2 opacity-0"
        style={{ visibility: "hidden" }}
      >
        {primaryItems.map((item) => (
          <span key={item.to} className={navLinkClassName(false)}>
            {item.label}
          </span>
        ))}
      </div>

      {primaryItems.slice(0, visibleCount).map((item) => (
        <HubNavLink
          key={item.to}
          item={item}
          app={app}
          pathname={pathname}
          hubNavDocument={hubNavDocument}
          onNavigate={onNavigate}
        />
      ))}

      {overflowItems.length > 0 ? (
        <div className="relative shrink-0">
          <button
            ref={moreTriggerRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            aria-label="More pages"
            onClick={() => setMoreOpen((current) => !current)}
            className={cn(
              navLinkClassName(overflowActive),
              labeledMore ? "gap-1 px-3" : "px-2",
            )}
          >
            {labeledMore ? <span>More</span> : null}
            <ChevronDown className={cn("h-4 w-4 transition-transform", moreOpen ? "rotate-180" : "")} aria-hidden />
          </button>
          {moreOpen && moreMenuBox
            ? createPortal(
                <ul
                  ref={moreMenuRef}
                  role="menu"
                  className="overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
                  style={{
                    position: "fixed",
                    top: moreMenuBox.top,
                    left: moreMenuBox.left,
                    width: moreMenuBox.width,
                    maxHeight: moreMenuBox.maxHeight,
                    zIndex: APP_SWITCHER_MENU_Z_INDEX,
                  }}
                >
                  {overflowItems.map((item) => {
                    const active = navItemActive(
                      app,
                      pathname,
                      item.to,
                      pathname === item.to || (item.end !== true && pathname.startsWith(`${item.to}/`)),
                    );
                    return (
                      <li key={item.to} role="none">
                        <NavLink
                          role="menuitem"
                          to={item.to}
                          end={item.end}
                          onClick={() => {
                            onNavigate();
                            setMoreOpen(false);
                          }}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 text-sm font-medium",
                            active
                              ? "bg-brand-50 text-brand-700"
                              : "text-slate-700 hover:bg-slate-50 hover:text-slate-900",
                          )}
                        >
                          {item.label}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>,
                document.body,
              )
            : null}
        </div>
      ) : null}
    </nav>
  );
}
